import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useModal } from '../../components/Modal/useModal';
import Header from '../../components/Header/Header';
import { Category, Sidebar } from '../../components/Sidebar/Sidebar';
import { ReactComponent as IdeaIcon } from '../../icons/idea.svg';
import ConfirmDemoModal from './ConfirmDemoModal';
import { useAppSelector } from '../../hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactComponent as FileIcon } from '../../icons/file-outline.svg';
import { ReactComponent as VATIcon } from '../../icons/vat.svg';
import { ReactComponent as InventoryIcon } from '../../icons/inventory.svg';
import { ReactComponent as GearIcon } from '../../icons/gear.svg';
import { ReactComponent as ServicesIcon } from '../../icons/services.svg';
import { ReactComponent as SalesIcon } from '../../icons/sales-arrow.svg';
import { ReactComponent as RecommendationsIcon } from '../../icons/bulb.svg';
import { ReactComponent as AmazonFeesIcon } from '../../icons/amazon-fees.svg';
import { SellerRoutesEnum } from '../../core/enums';
import { NavLink } from 'react-router-dom';
import { ReactComponent as GridIcon } from '../../icons/grid.svg';
import AggregatorDropdown from '../../pages/Seller/AggregatorDropdown';
import { ReactComponent as InvoiceBookIcon } from '../../icons/invoice-book.svg';
import { ReactComponent as TurnoverDataIcon } from '../../icons/turnover-data.svg';
import { useThreshold } from '../../context/threshold-context';
import Button from '../../components/Button';

export default function SellerLayout({ children }: PropsWithChildren<{}>) {
  const { t } = useTranslation();

  const location = useLocation();
  const isCountryDetailsPage = location.pathname.startsWith(
    SellerRoutesEnum.CountryDetails.replace('/:id', '')
  );
  const companies = useAppSelector((s) => s.user.companies);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);

  const { vs2912CpPrototypeV2 } = useFlags();
  const { threshold } = useThreshold();
  const params = new URLSearchParams(location.search);
  const unsubscribe = params.get('unsubscribe');

  const {
    isModalOpened: isConfirmDemoModalOpened,
    closeModal: closeConfirmDemoModal,
    openModal: openConfirmDemoModal,
  } = useModal();

  const categories: Category[] = [
    ...(vs2912CpPrototypeV2
      ? [
          {
            name: 'Filing',
            links: [
              {
                label: 'Filing',
                icon: <FileIcon />,
                to: SellerRoutesEnum.Filing,
              },
              ...(activeCompany?.ManualUploadNeeded
                ? [
                    {
                      label: 'Turnover data',
                      icon: <TurnoverDataIcon />,
                      to: SellerRoutesEnum.DataFiles,
                    },
                  ]
                : []),

              {
                label: 'Purchase prices',
                icon: <InventoryIcon />,
                to: SellerRoutesEnum.PurchasePrices,
              },
            ],
          },
          {
            name: 'Documents',
            links: [
              {
                label: 'Inventory',
                icon: <InventoryIcon />,
                to: SellerRoutesEnum.Inventory,
              },
              {
                label: 'Invoice Book',
                icon: <InvoiceBookIcon />,
                to: SellerRoutesEnum.InvoiceBook,
              },
            ],
          },
          {
            name: 'Setup',
            links: [
              ...(threshold > 0
                ? [
                    {
                      label: 'Overview',
                      icon: <GearIcon />,
                      to: SellerRoutesEnum.SetupOverview,
                    },
                  ]
                : []),
              {
                label: 'Tax Details',
                icon: <VATIcon />,
                to: SellerRoutesEnum.VAT,
              },
            ],
          },
          {
            name: 'Insights',
            links: [
              {
                label: 'Sales',
                icon: <SalesIcon />,
                to: SellerRoutesEnum.Sales,
              },
              {
                label: 'Growth tips',
                icon: <RecommendationsIcon />,
                to: SellerRoutesEnum.Recommendations,
              },
              {
                label: 'Amazon fees',
                icon: <AmazonFeesIcon />,
                to: SellerRoutesEnum.AmazonFees,
              },
            ],
          },
          {
            name: 'Account',
            links: [
              {
                label: 'Services',
                icon: <ServicesIcon />,
                to: SellerRoutesEnum.Services,
              },
            ],
          },
        ]
      : [
          {
            name: 'Filing',
            links: [
              {
                label: 'Filing',
                icon: <FileIcon />,
                to: SellerRoutesEnum.Filing,
              },
              ...(activeCompany?.ManualUploadNeeded
                ? [
                    {
                      label: 'Turnover data',
                      icon: <TurnoverDataIcon />,
                      to: SellerRoutesEnum.DataFiles,
                    },
                  ]
                : []),
              {
                label: 'Purchase prices',
                icon: <InventoryIcon />,
                to: SellerRoutesEnum.PurchasePrices,
              },
            ],
          },
          {
            name: 'Setup',
            links: [
              ...(threshold > 0
                ? [
                    {
                      label: 'Overview',
                      icon: <GearIcon />,
                      to: SellerRoutesEnum.SetupOverview,
                    },
                  ]
                : []),
              {
                label: 'Tax Details',
                icon: <VATIcon />,
                to: SellerRoutesEnum.VAT,
              },
            ],
          },
        ]),
  ];

  const isSidebarShown = !isCountryDetailsPage && !unsubscribe;

  return (
    <div className='d-flex'>
      {isSidebarShown && (
        <Sidebar
          categories={categories}
          header={
            <>
              <div className='pt-2'>
                {companies.length > 1 ? (
                  <AggregatorDropdown />
                ) : (
                  <div className='fw-bold text-truncate mb-2'>
                    {activeCompany?.name}
                  </div>
                )}
              </div>
              <NavLink
                to='/'
                className='Sidebar__link text-decoration-none d-flex align-items-center mx-n2 mb-2 py-1 ps-2 mb-3'
                exact
                data-testid='sidebar-link-Global overview'
              >
                <GridIcon />
                <span className='ms-1'>{t('Global overview')}</span>
              </NavLink>
            </>
          }
        />
      )}
      <div className='flex-grow-1'>
        <div className='d-flex flex-column'>
          <div className='min-vh-100 d-flex flex-column'>
            <Header
              btn={
                !isCountryDetailsPage ? (
                  <Button
                    styling='outline'
                    className='py-1 Header__btn me-2 d-flex align-items-center'
                    onClick={openConfirmDemoModal}
                  >
                    <IdeaIcon className='me-1' />
                    {t('Share ideas')}
                  </Button>
                ) : undefined
              }
              logo={!!unsubscribe}
              backBtn={isCountryDetailsPage}
              label={isCountryDetailsPage ? t('Country details') : undefined}
            />
            {children}
            <ConfirmDemoModal
              closeModal={closeConfirmDemoModal}
              isModalOpened={isConfirmDemoModalOpened}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
